/**
 * AuditTimeline — timeline cronologica de eventos de auditoria.
 *
 * Agrupa por dia (`groupBy="day"`) ou exibe lista flat. Cada evento usa
 * tone-color por tipo de acao para sinalizar criticidade visualmente
 * (P11 — cor com significado funcional).
 *
 * Props:
 * - `events`: [{ id, timestamp, acao, ator_email, alvo_label, resultado }]
 * - `onEventClick(id)`: opcional — abre drawer de detalhe
 * - `groupBy`: "day" | "none" — default "day"
 * - `emptyMessage`: string
 */
import React from "react";
import styled from "styled-components";

import { getToneTokens } from "../../styles/shared";

const TONE_POR_ACAO = {
  login_ok: "success",
  login_falha: "danger",
  logout: "neutral",
  senha_alterada: "info",
  senha_resetada: "warning",
  usuario_criado: "success",
  usuario_atualizado: "info",
  usuario_desativado: "warning",
  usuario_excluido: "danger",
  cargo_criado: "info",
  cargo_atualizado: "info",
  cargo_excluido: "danger",
  permissao_cargo_alterada: "info",
  permissao_usuario_alterada: "info",
  permissao_negada: "danger",
  pode_ver_precos_alterado: "info",
  mfa_enrolled: "success",
  mfa_disabled: "warning",
  mfa_falhou: "danger",
  mfa_recovery_usado: "warning",
  sod_violacao_bloqueada: "danger",
  sod_violacao_aceita: "warning",
  conflito_sod_criado: "info",
  conflito_sod_atualizado: "info",
  conflito_sod_excluido: "warning",
};

const TONE_POR_RESULTADO = {
  ok: "neutral",
  falha: "danger",
  bloqueado: "danger",
};

function obterTone(evento) {
  if (TONE_POR_RESULTADO[evento.resultado] === "danger") return "danger";
  return TONE_POR_ACAO[evento.acao] || "neutral";
}

function formatarHorario(iso) {
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatarData(iso) {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function formatarAcao(acao) {
  return String(acao || "")
    .split("_")
    .map((p) => (p.length ? p[0].toUpperCase() + p.slice(1) : ""))
    .join(" ");
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const GroupHeader = styled.h4`
  margin: 4px 0 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.textSecondary};
`;

const Lista = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.li`
  display: grid;
  grid-template-columns: 60px 8px 1fr;
  gap: 12px;
  align-items: start;
`;

const Hora = styled.time`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.textSecondary};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const Marcador = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 6px;
  background: ${({ theme, $tone }) => getToneTokens(theme, $tone).text};
  border: 2px solid ${({ theme, $tone }) => getToneTokens(theme, $tone).background};
  box-shadow: 0 0 0 1px ${({ theme, $tone }) => getToneTokens(theme, $tone).border};
`;

const Conteudo = styled.button`
  appearance: none;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  padding: 8px 12px;
  text-align: left;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  color: ${({ theme }) => theme.textPrimary};
  display: flex;
  flex-direction: column;
  gap: 2px;

  &:hover {
    ${({ $clickable, theme }) =>
      $clickable ? `background: ${theme.surfaceAlt};` : ""}
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
`;

const Acao = styled.strong`
  font-size: 0.85rem;
  font-weight: 600;
`;

const Detalhes = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.textSecondary};
`;

const Empty = styled.p`
  margin: 0;
  padding: 18px;
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.85rem;
`;

function agruparPorDia(eventos) {
  const grupos = new Map();
  for (const ev of eventos) {
    const dia = formatarData(ev.timestamp);
    if (!grupos.has(dia)) grupos.set(dia, []);
    grupos.get(dia).push(ev);
  }
  return [...grupos.entries()];
}

function AuditTimeline({
  events = [],
  onEventClick,
  groupBy = "day",
  emptyMessage = "Sem eventos no periodo.",
}) {
  if (!events.length) return <Empty>{emptyMessage}</Empty>;

  const grupos =
    groupBy === "day" ? agruparPorDia(events) : [[null, events]];

  return (
    <Wrap>
      {grupos.map(([dia, lista], idx) => (
        <div key={dia || idx}>
          {dia && <GroupHeader>{dia}</GroupHeader>}
          <Lista>
            {lista.map((ev) => {
              const tone = obterTone(ev);
              const clickable = Boolean(onEventClick);
              return (
                <Item key={ev.id}>
                  <Hora dateTime={ev.timestamp}>{formatarHorario(ev.timestamp)}</Hora>
                  <Marcador $tone={tone} aria-hidden="true" />
                  <Conteudo
                    type="button"
                    $clickable={clickable}
                    onClick={clickable ? () => onEventClick(ev.id) : undefined}
                    aria-label={`Evento ${ev.acao} em ${formatarHorario(ev.timestamp)}`}
                  >
                    <Acao>{formatarAcao(ev.acao)}</Acao>
                    <Detalhes>
                      {ev.ator_email || "—"}
                      {ev.alvo_label ? ` · ${ev.alvo_label}` : ""}
                      {ev.resultado && ev.resultado !== "ok"
                        ? ` · ${ev.resultado}`
                        : ""}
                    </Detalhes>
                  </Conteudo>
                </Item>
              );
            })}
          </Lista>
        </div>
      ))}
    </Wrap>
  );
}

export default AuditTimeline;
